import { useEffect, useRef } from 'react';
import { Map, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { toast } from '../UpToast';

function MapView({
  coords,
  setAddress,
}: {
  coords: { lat: number; lng: number };
  setAddress: (address: google.maps.places.Place | null) => void;
}) {
  const places = useMapsLibrary('places');

  const fetchAddressForCenter = async (center: {
    lat: number;
    lng: number;
  }) => {
    try {
      const geocoder = new google.maps.Geocoder();
      const { results } = await geocoder.geocode({
        location: center,
      });

      if (places && results?.[0]?.place_id) {
        const place = new places.Place({ id: results?.[0]?.place_id });
        await place.fetchFields({
          fields: [
            'location',
            'displayName',
            'formattedAddress',
            'addressComponents',
          ],
        });
        setAddress(place);
      }
    } catch {
      toast.info('Something went wrong. Could not fetch address details.');
    }
  };

  return (
    <div className="relative w-full h-[360px]">
      <Map
        defaultZoom={18}
        disableDefaultUI
        clickableIcons={false}
        defaultCenter={coords}
        gestureHandling="greedy"
        mapId="92e56ef8275d4063517c824e">
        <MapController
          center={coords}
          onDragEnd={center => {
            void fetchAddressForCenter(center);
          }}
        />
      </Map>
      <MapMarkerIcon />
    </div>
  );
}

function MapController({
  center,
  onDragEnd,
}: {
  center: { lat: number; lng: number };
  onDragEnd: (center: { lat: number; lng: number }) => void;
}) {
  const map = useMap();
  const isDraggingRef = useRef(false);

  useEffect(() => {
    if (!map || isDraggingRef.current) return;
    map.panTo(center);
  }, [map, center]);

  useEffect(() => {
    if (!map) return;
    const dragStartListener = map.addListener('dragstart', () => {
      isDraggingRef.current = true;
    });
    const dragEndListener = map.addListener('dragend', () => {
      isDraggingRef.current = false;
    });
    const idleListener = map.addListener('idle', () => {
      const newCenter = map.getCenter();
      if (newCenter) {
        onDragEnd({
          lat: newCenter.lat(),
          lng: newCenter.lng(),
        });
      }
    });

    return () => {
      google.maps.event.removeListener(idleListener);
      google.maps.event.removeListener(dragEndListener);
      google.maps.event.removeListener(dragStartListener);
    };
  }, [map, onDragEnd]);

  return null;
}

function MapMarkerIcon() {
  return (
    <div className="absolute z-1 top-[48%] left-1/2 pointer-events-none transform -translate-x-1/2 -translate-y-full">
      <svg
        width="56"
        height="56"
        viewBox="0 0 56 56"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#clip0_5851_12376)">
          <g filter="url(#filter0_d_5851_12376)">
            <path
              d="M27.9998 4.6665C18.9792 4.6665 11.6665 11.9792 11.6665 20.9998C11.6665 32.6665 27.9998 51.3332 27.9998 51.3332C27.9998 51.3332 44.3332 32.6665 44.3332 20.9998C44.3332 11.9792 37.0205 4.6665 27.9998 4.6665ZM27.9998 26.8332C24.7775 26.8332 22.1665 24.2222 22.1665 20.9998C22.1665 17.7775 24.7775 15.1665 27.9998 15.1665C31.2222 15.1665 33.8332 17.7775 33.8332 20.9998C33.8332 24.2222 31.2222 26.8332 27.9998 26.8332Z"
              fill="black"
            />
          </g>
          <circle cx="27.9998" cy="20.9998" r="5.83333" fill="white" />
        </g>
        <defs>
          <filter
            id="filter0_d_5851_12376"
            x="8.6665"
            y="3.6665"
            width="42.6665"
            height="56.6665"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dx="2" dy="4" />
            <feGaussianBlur stdDeviation="2.5" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_5851_12376"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow_5851_12376"
              result="shape"
            />
          </filter>
          <clipPath id="clip0_5851_12376">
            <rect width="56" height="56" fill="white" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
}

export default MapView;
